import type { RootState } from '@/rootReducer';
import ContextMenu from '@/components/ContextMenu/ContextMenu';
import Popup, { PopupAlign, PopupTrigger } from '@/components/Popup/Popup';
import RichEditor from '@/components/RichEditor/RichEditor';
import type { Business } from '@/modules/dashboard/dashboardTypes';
import {
	DropDownDark,
	EmailPrimary,
	EmailSecondary,
	BouncedEmail,
	CloseThin,
	Lightbulb,
	ExclamationMarkBlack,
	ClockBlack,
	QuestionMarkCircle,
	Stale,
	ChatGPT,
	ExclamationMarkRed
} from '@/resources/assets/images';
import insightFilledColoredIcon from '@/assets/images/insight-filled-colored.png';
import noop from '@/utils/noop';
import encodeUrl from '@/utils/encodeUrl';
import { sendAnalytics } from '@/services/analyticsService';
import Loader, { Status } from '@/components/Loader/Loader';
import classNames from 'classnames';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import Autosuggest from 'react-autosuggest';
import moment from 'moment';
import isEmail from 'validator/lib/isEmail';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useFreeTrialModal } from '@/modules/freeTrial/FreeTrialModalContext';
import type {
	DirectEmail,
	EmailSuggestion,
	EmailTemplate
} from '../../emailTypes';

import './EmailForm.scss';
import {
	emailClearRevertAITemplate,
	emailGetAISnippets,
	emailGetEmailContactedStatus,
	emailRegenAISnippet,
	emailSendScheduledEmailClearFailureStatus
} from '../../emailActions';
import { UserRole } from '@/modules/user/userTypes';

type Props = {
	emailName?: string;
	emailSubject: string;
	emailBcc?: string;
	emailBody: string;
	emailTo?: string;
	forEditing?: boolean;
	size?: 'md' | 'lg';
	directEmail?: DirectEmail;
	emailToSuggestions?: EmailSuggestion[];
	activeBusiness?: Business;
	selectedDateAndTime: Dayjs | null;
	templateWarning?: boolean;
	testShowTemplate?: boolean;

	onChange?: (
		subject: string,
		body: string,
		to: string,
		bcc?: string,
		name?: string
	) => void;

	validation: {
		email?: string;
		bcc?: string;
		name?: string;
		subject: string;
		body: string;
	};
	disabled?: boolean;
	selectedTemplate?: EmailTemplate;
	setSelectedTemplate?: (template: EmailTemplate) => void;
	emailStatus?: number;
	to: string;
	setTo: (x: string) => void;
	setShowAiLoadingModal: (x: boolean) => void;
	rotateTips: () => void;
};

export default function EmailForm({
	emailName = '',
	emailSubject,
	emailBcc,
	emailBody,
	emailTo,
	emailToSuggestions,
	forEditing,
	size,
	onChange,
	validation,
	disabled = false,
	directEmail,
	activeBusiness,
	selectedTemplate,
	setSelectedTemplate,
	emailStatus,
	to,
	setTo,
	setShowAiLoadingModal,
	rotateTips
}: Props): JSX.Element {
	const [
		emailSubjectForReply,
		emailContacted,
		contactedDetails,
		contactedLoading,
		user,
		templates,
		staleTemplates,
		aiText,
		aiCredits,
		aiLoading,
		aiRegenerateLoading,
		aiRegenerateFailure,
		aiRegenerated,
		aiTemplates,
		revertAiTemplate,
		cancelScheduledEmailSuccess,
		scheduledEmailActivity,
		scheduledEmailFailure,
		listSelected,
		emailFailure
	] = useSelector(
		(state: RootState) => [
			state.email.getIn(['email', 'replySubject']),
			state.email.getIn(['contacted', 'emailContacted']),
			state.email.getIn(['contacted', 'contactedDetails']),
			state.email.getIn(['contacted', 'loading']),
			state.user.getIn(['authorize', 'user']).toJS(),
			state.email.getIn(['templates', 'data']).toJS(),
			state.email.getIn(['templates', 'stale']).toJS(),
			state.email.getIn(['ai', 'text']).toJS(),
			state.email.getIn(['ai', 'credits']),
			state.email.getIn(['ai', 'loading']),
			state.email.getIn(['ai', 'regenerate', 'loading']).toJS(),
			state.email.getIn(['ai', 'regenerate', 'failure']),
			state.email.getIn(['ai', 'regenerate', 'data']).toJS(),
			state.email.getIn(['ai', 'templates']).toJS(),
			state.email.getIn(['ai', 'revertTemplate']),
			state.email.getIn(['email', 'scheduled', 'cancel', 'success']),
			state.email.getIn(['email', 'scheduled', 'activity']).toJS(),
			state.email.getIn(['email', 'scheduled', 'failure']),
			state.dashboard.getIn(['lists', 'selected']).toJS(),
			state.email.getIn(['email', 'failure'])
		],
		shallowEqual
	);

	const subjectRef = useRef<HTMLInputElement>(null);
	const toInputRef = useRef<HTMLInputElement>(null);
	const [subject, setSubject] = useState<string>(emailSubject);
	const [name, setName] = useState<string>(emailName);
	const [body, setBody] = useState<string>(emailBody);
	const [titleInputValue, setTitleInputValue] = useState<string>('');

	const [bcc, setBcc] = useState<string | undefined>(emailBcc);
	const [bodyContextMenuValue, setBodyContextMenuValue] = useState<
		string | undefined
	>();

	const [showBCC, setShowBCC] = useState<boolean>(false);

	const [emailSuggestions, setEmailSuggestions] = useState<EmailSuggestion[]>(
		[]
	);
	const [showSpamSuggestion, setShowSpamSuggestion] =
		useState<boolean>(false);
	const [enableSpamSuggestion, setEnableSpamSuggestion] =
		useState<boolean>(true);
	const [showSubjectLengthSuggestion, setShowSubjectLengthSuggestion] =
		useState<boolean>(false);
	const [enableSubjectLengthSuggestion, setEnableSubjectLengthSuggestion] =
		useState<boolean>(true);
	const subjectSuggestedLengthRange = {
		start: 2,
		end: 4
	};
	const [spamWordsInSubjectLine, setSpamWordsInSubjectLine] = useState<
		string[]
	>([]);
	const [openTemplatesDropdown, setOpenTemplatesDropdown] =
		useState<boolean>(false);
	const [aiTemplateToSwitch, setAiTemplateToSwitch] = useState<
		EmailTemplate | undefined
	>(undefined);

	const dispatch = useDispatch();

	const getEmailContactedStatus = (toEmail: string) => {
		dispatch(emailGetEmailContactedStatus({ toEmail }));
	};
	const sendScheduledEmailClearFailureStatus = () =>
		dispatch(emailSendScheduledEmailClearFailureStatus());
	const getAISnippets = (
		businessIds: string[],
		listId: string,
		emailTemplate: EmailTemplate
	) => dispatch(emailGetAISnippets({ businessIds, listId, emailTemplate }));
	const regenAISnippet = (
		businessId: string,
		listId: string,
		emailTemplate: EmailTemplate,
		aiSnippetsId: string
	) =>
		dispatch(
			emailRegenAISnippet({
				businessId,
				listId,
				emailTemplate,
				aiSnippetsId
			})
		);
	const clearRevertAITemplate = () => dispatch(emailClearRevertAITemplate());

	const setToEmail = () => {
		if (directEmail && directEmail.email) {
			setTo(directEmail.email);
		} else if (emailToSuggestions && emailToSuggestions?.length > 0) {
			for (let i = 0; i < emailToSuggestions?.length; i++) {
				if (emailToSuggestions[i].isPrimary) {
					setTo(emailToSuggestions[i].email);
				}
			}
		}
	};

	const updatePrimary = emailsSuggestions => {
		const suggestions = emailsSuggestions;
		if (suggestions && suggestions?.length > 0) {
			for (let i = 0; i < suggestions?.length; i++) {
				if (suggestions[i] === to) {
					suggestions[i].isPrimary = true;
				} else {
					suggestions[i].isPrimary = false;
				}
			}
		}
		setEmailSuggestions(suggestions ?? []);
	};

	const spamWordsMessage = () => {
		if (spamWordsInSubjectLine?.length === 1) {
			return `"${spamWordsInSubjectLine[0]}" is commonly caught by spam filters.`;
		}

		if (spamWordsInSubjectLine?.length === 2) {
			return `"${spamWordsInSubjectLine[0]}" and "${spamWordsInSubjectLine[1]}" are commonly caught by spam filters.`;
		}

		if (spamWordsInSubjectLine?.length > 2) {
			const modifiedSpamList = spamWordsInSubjectLine.map(
				word => `"${word}"`
			);
			modifiedSpamList[modifiedSpamList?.length - 1] =
				`and ${modifiedSpamList[modifiedSpamList?.length - 1]}`;
			const quotedSpamWords = modifiedSpamList.join(', ');
			return `${quotedSpamWords} are commonly caught by spam filters.`;
		}
	};

	const contactedCalloutText = () => {
		const uniqueContacts = contactedDetails?.length;

		let sentDate;
		if (uniqueContacts) {
			sentDate = moment(contactedDetails[0]?.lastContactedDate);
		}
		const currentDate = moment(new Date());
		const hoursDiff = currentDate.diff(sentDate, 'hours');
		const daysDiff = currentDate.diff(sentDate, 'days');

		const formatDate = inputDate => {
			const date = new Date(inputDate);
			const month = (date.getMonth() + 1).toString().padStart(2, '0');
			const day = date.getDate().toString().padStart(2, '0');
			const year = date.getFullYear();
			return `${month}/${day}/${year}`;
		};

		let text;
		if (hoursDiff < 24) {
			text = `less than a day ago`;
		} else if (hoursDiff >= 24 && hoursDiff < 48) {
			text = 'a day ago';
		} else if (hoursDiff >= 48) {
			text = `${daysDiff} days ago`;
		} else {
			text = 'already';
		}

		let contactedText;
		if (uniqueContacts >= 1) {
			if (uniqueContacts === 0) {
				contactedText = '';
			} else if (contactedDetails[0]?.email !== user.email) {
				contactedText = `This email was last contacted by ${contactedDetails[0]?.email} on ${formatDate(contactedDetails[0]?.lastContactedDate)}.`;
			} else if (contactedDetails[0]?.email === user.email) {
				contactedText = `You emailed this business on Resquared ${text}.`;
			}
		}
		return (
			<div>
				{contactedText}&nbsp;
				{activeBusiness?.facebook?.messengerUrl && (
					<a
						href={encodeUrl(
							`https://${activeBusiness?.facebook?.messengerUrl}`
						)}
						rel="noopener noreferrer"
						title="Facebook messenger"
						target="_blank"
						className="contacted-link"
						onClick={() => {
							if (user && user.email) {
								sendAnalytics({
									type: 'track',
									userId: user.email,
									eventName:
										'User clicked FB messenger link in email callout',
									properties: {
										businessAddress:
											activeBusiness?.address,
										businessId:
											activeBusiness?._id ??
											activeBusiness?.id,
										businessName: activeBusiness?.name
									}
								});
							}
						}}
					>
						Try Messenger Instead
					</a>
				)}
			</div>
		);
	};

	const checkSoftBounced = () => {
		if (
			(to === activeBusiness?.email &&
				activeBusiness?.emailLowPriority) ||
			(to === activeBusiness?.facebook?.email &&
				activeBusiness?.facebook?.emailLowPriority) ||
			(to === activeBusiness?.web?.email &&
				activeBusiness?.web?.emailLowPriority) ||
			activeBusiness?.userAddedData?.some(data => {
				if (data?.type !== 'email') {
					return false;
				}

				if (data?.data === to && data?.emailLowPriority) {
					return true;
				}

				return false;
			})
		) {
			return true;
		}

		return false;
	};

	const templatesDropdownRef = useRef<HTMLDivElement>(null);

	const closeTemplatesDropdown = e => {
		if (!templatesDropdownRef?.current?.contains(e?.target)) {
			setOpenTemplatesDropdown(false);
		}
	};

	useEffect(() => {
		window.addEventListener('click', closeTemplatesDropdown);

		return () =>
			window.removeEventListener('click', closeTemplatesDropdown);
	}, []);

	useEffect(() => {
		if (to && user?.email && isEmail(to)) {
			setTimeout(() => {
				getEmailContactedStatus(to);
			}, 1);
		}
	}, [to, activeBusiness?._id, cancelScheduledEmailSuccess]);

	useEffect(() => {
		updatePrimary(emailToSuggestions);

		if (!directEmail && setTo) setTo(String(emailTo).toLowerCase() ?? '');
	}, [emailToSuggestions]);

	useEffect(() => {
		setToEmail();
	}, [directEmail]);

	useEffect(() => {
		if (emailSubjectForReply) {
			setSubject(emailSubjectForReply);
		}
	}, [emailSubjectForReply]);

	useEffect(() => {
		setBody(emailBody);
		setSubject(emailSubject);
		setName(emailName);
		setBcc(emailBcc);
	}, [emailSubject, emailBody, emailName, emailBcc]);

	useEffect(() => {
		if (onChange) {
			onChange(subject, body, to, bcc, name);
		}
	}, [subject, body, to, bcc, name]);

	useEffect(() => {
		setBodyContextMenuValue(undefined);
	}, [bodyContextMenuValue]);

	const replaceVariables = (value: string): string =>
		value
			.replaceAll(/\[business_name\]/gi, activeBusiness?.name ?? '')
			.replaceAll(/\[list_name\]/gi, listSelected?.name ?? '');

	useEffect(() => {
		if (scheduledEmailFailure) {
			sendScheduledEmailClearFailureStatus();
		}
	}, [activeBusiness]);

	const fetchingAiSnippets = (template: EmailTemplate) => {
		if (!template?.id || !listSelected?.businessIds?.length) return;

		const aiTextCount = Object.keys(
			aiText?.[listSelected?.id]?.[template?.id] ?? {}
		)?.length;

		const batchSize = 50;

		const start = Math.floor(aiTextCount / batchSize) * batchSize;
		const end =
			aiCredits >= batchSize
				? (Math.ceil(aiTextCount / batchSize) + 1) * batchSize
				: start + aiCredits;

		const selectedBusinessIds =
			listSelected?.businessIds?.slice(start, end) || [];

		getAISnippets([...selectedBusinessIds], listSelected?.id, template);
	};

	useEffect(() => {
		if (
			!selectedTemplate ||
			!selectedTemplate?.id ||
			!selectedTemplate?.body?.includes('[ai_snippet]') ||
			!activeBusiness ||
			aiCredits <= 0 ||
			aiLoading
		) {
			return;
		}

		const activeSnippet =
			aiText?.[listSelected?.id]?.[selectedTemplate?.id] ?? {};

		if (
			!directEmail &&
			listSelected?.businessIds?.length > 0 &&
			Object.keys(activeSnippet)?.length <
				listSelected?.businessIds?.length
		) {
			fetchingAiSnippets(selectedTemplate);
		}

		if (
			directEmail &&
			!activeSnippet?.[activeBusiness?._id] &&
			activeBusiness._id
		) {
			getAISnippets(
				[activeBusiness._id],
				listSelected?.id,
				selectedTemplate
			);
		}
	}, [aiLoading]);

	useEffect(() => {
		if (revertAiTemplate) {
			setSelectedTemplate(aiTemplates?.[`${selectedTemplate?.id}`]);
			clearRevertAITemplate();
		}
	}, [revertAiTemplate]);

	const { openFreeTrialModal } = useFreeTrialModal();

	const handleOpenFreeTrialModal = () => {
		openFreeTrialModal();
	};

	const isUserFreeTier = user.role === UserRole.Free || user.role === UserRole.PreTrial;

	return (
		<div
			className={`emailForm-wrapper ${emailTo === undefined ? 'single-template-view' : ''}`}
		>
			<div
				className={`contacted ${
					size === 'md' &&
					emailContacted &&
					emailStatus !== Status.FAILURE &&
					!contactedLoading &&
					!scheduledEmailActivity[`${activeBusiness?._id}`] &&
					!scheduledEmailFailure &&
					String(emailFailure).trim() !== 'Email limit reached'
						? ''
						: 'hide'
				} ${directEmail ? 'direct-email' : ''} `}
			>
				<Lightbulb height={28} width={28} className="contacted-icon" />
				{contactedCalloutText()}
			</div>
			<div
				className={`scheduled-send-banner ${scheduledEmailActivity[`${activeBusiness?._id}`] ? '' : 'hide'}`}
			>
				<ClockBlack className="scheduled-send-banner-icon" />
				<span>
					Email is scheduled via Resquared to this business on&nbsp;
					<span className="scheduled-send-banner-date">
						{`${dayjs(scheduledEmailActivity[`${activeBusiness?._id}`]?.scheduleDate, 'MM/DD/YYYY, h:mm:ss A')?.format('ddd, MMM D')} at ${dayjs(scheduledEmailActivity[`${activeBusiness?._id}`]?.scheduleDate, 'MM/DD/YYYY, h:mm:ss A')?.format('hh:mm a')}.`}
					</span>
					<span className="scheduled-send-banner-info">
						(Different from my scheduled time?)
					</span>
					<span className="scheduled-send-banner-hovertext">
						Resquared automatically spaces out your emails by at
						least a minute to improve deliverability.
					</span>
				</span>
			</div>
			<div
				className={`scheduled-send-failure-banner ${scheduledEmailFailure && !scheduledEmailActivity[`${activeBusiness?._id}`] ? '' : 'hide'}`}
			>
				<ExclamationMarkBlack
					height={28}
					width={28}
					className="scheduled-send-failure-banner-icon"
				/>
				{scheduledEmailFailure ===
				'Email is already scheduled for this business' ? (
					<span>
						You have already scheduled an email for this business.
					</span>
				) : (
					<span>
						Email scheduling failed. If you continue seeing this,
						please contact&nbsp;
						<a
							href="mailto:support@re2.ai"
							className="scheduled-send-failure-banner-link"
						>
							customer support
						</a>
						.
					</span>
				)}
			</div>
			<div
				className={`scheduled-send-failure-banner ${
					String(emailFailure).trim() === 'Email limit reached' &&
					!scheduledEmailActivity[`${activeBusiness?._id}`]
						? ''
						: 'hide'
				} `}
			>
				<ExclamationMarkBlack
					height={28}
					width={28}
					className="scheduled-send-failure-banner-icon"
				/>
				<span>
					You have reached your email limit for today. Please try
					again tomorrow.
				</span>
			</div>
			{/*  */}
			<div
				className={`scheduled-send-failure-banner ${
					String(emailFailure).trim() ===
						'Email is already scheduled for this business' &&
					!scheduledEmailActivity[`${activeBusiness?._id}`]
						? ''
						: 'hide'
				} `}
			>
				<ExclamationMarkBlack
					height={28}
					width={28}
					className="scheduled-send-failure-banner-icon"
				/>
				<span>
					An email is already scheduled for this business.&nbsp;
				</span>
			</div>
			<div
				className={`scheduled-send-failure-banner ${
					String(emailFailure).trim() ===
						'Cannot schedule for this time unit' &&
					!scheduledEmailActivity[`${activeBusiness?._id}`]
						? ''
						: 'hide'
				} `}
			>
				<ExclamationMarkBlack
					height={28}
					width={28}
					className="scheduled-send-failure-banner-icon"
				/>
				<span>Cannot schedule for this time unit.&nbsp;</span>
			</div>
			<div
				className={`scheduled-send-failure-banner ${
					String(emailFailure).trim() ===
						'Email limit for this time unit has been reached' &&
					!scheduledEmailActivity[`${activeBusiness?._id}`]
						? ''
						: 'hide'
				} `}
			>
				<ExclamationMarkBlack
					height={28}
					width={28}
					className="scheduled-send-failure-banner-icon"
				/>
				<span>
					Email limit for this time unit has been reached.&nbsp;
				</span>
			</div>
			<div
				className={`scheduled-send-failure-banner ${
					String(emailFailure).trim() ===
						'Cannot schedule for this day' &&
					!scheduledEmailActivity[`${activeBusiness?._id}`]
						? ''
						: 'hide'
				} `}
			>
				<ExclamationMarkBlack
					height={28}
					width={28}
					className="scheduled-send-failure-banner-icon"
				/>
				<span>Cannot schedule for this day.&nbsp;</span>
			</div>
			<div
				className={`scheduled-send-failure-banner ${
					String(emailFailure).trim() ===
						'Email sending failed because the template has gone stale' &&
					!scheduledEmailActivity[`${activeBusiness?._id}`]
						? ''
						: 'hide'
				} `}
			>
				<ExclamationMarkBlack
					height={28}
					width={28}
					className="scheduled-send-failure-banner-icon"
				/>
				<span>
					Email sending failed because the template has gone
					stale.&nbsp;
				</span>
			</div>
			<div
				className={`scheduled-send-failure-banner ${
					String(emailFailure).trim() ===
						'Email sending failed because nylas cipher or nylas key does not exist' &&
					!scheduledEmailActivity[`${activeBusiness?._id}`]
						? ''
						: 'hide'
				} `}
			>
				<ExclamationMarkBlack
					height={28}
					width={28}
					className="scheduled-send-failure-banner-icon"
				/>
				<span>
					Email sending failed. Please re-verify your email to
					continue&nbsp;
				</span>
			</div>
			{/*  */}
			{setSelectedTemplate && size === 'md' && (
				<div
					className={`emailForm-template-dropdown-container ${directEmail ? 'direct-email' : ''}`}
				>
					<span className="emailForm-template-dropdown-header">
						Current Template:
					</span>
					<div className="emailForm-template-dropdown-hovertext">
						We recommend <span>changing your template</span> every
						50 emails to avoid ending up in spam.
					</div>
					<div
						ref={templatesDropdownRef}
						className={`emailForm-template-dropdown-input ${scheduledEmailActivity[`${activeBusiness?._id}`] ? 'disabled' : ''} `}
						onClick={() => {
							if (
								scheduledEmailActivity[`${activeBusiness?._id}`]
							) {
								return;
							}

							setOpenTemplatesDropdown(!openTemplatesDropdown);
						}}
					>
						<span>{selectedTemplate?.name}</span>
						<DropDownDark
							className={`emailForm-template-dropdown-input-icon ${openTemplatesDropdown ? 'opened' : ''}`}
						/>
					</div>
					<div
						className={`emailForm-template-dropdown ${openTemplatesDropdown ? 'opened' : ''}`}
					>
						<div className="emailForm-template-dropdown-items">
							{templates &&
								templates.map((template: EmailTemplate) => (
									<div
										className={`emailForm-template-dropdown-row ${selectedTemplate?.id === template?.id ? 'selected' : ''} `}
										key={template?.id}
										onClick={e => {
											if (
												selectedTemplate?.id ===
												template?.id
											) {
												return;
											}

											if (
												template.body?.includes(
													'[ai_snippet]'
												) &&
												directEmail &&
												!aiText?.[
													`${listSelected?.id}`
												]?.[`${template?.id}`]?.[
													`${activeBusiness?._id}`
												] &&
												aiCredits > 0
											) {
												setAiTemplateToSwitch(template);
												return;
											}

											if (
												template.body?.includes(
													'[ai_snippet]'
												) &&
												!directEmail &&
												Object.keys(
													aiText?.[
														`${listSelected?.id}`
													]?.[`${template?.id}`] ?? {}
												)?.length <
													listSelected?.businessIds
														?.length &&
												aiCredits > 0
											) {
												setAiTemplateToSwitch(template);
												return;
											}

											setSelectedTemplate(template);
										}}
									>
										{template.body?.includes(
											'[ai_snippet]'
										) && <ChatGPT />}
										<div className="emailForm-template-dropdown-row-title">
											{template.name}
										</div>
									</div>
								))}
						</div>
					</div>
				</div>
			)}
			{aiTemplateToSwitch && (
				<div className="ai-credits-confirmation">
					<div className="ai-credits-confirmation-left">
						<div className="ai-credits-confirmation-left-limits">
							AI credits left
							<span className="ai-credits-confirmation-left-limits-credits">
								{aiCredits}
							</span>
							Businesses
							<span className="ai-credits-confirmation-left-limits-businesses">
								{directEmail
									? 1
									: listSelected.businessIds?.length}
							</span>
						</div>
						<div className="ai-credits-confirmation-left-note">
							Note: Switching to another AI snippet template will
							immediately use up more AI credits.
						</div>
					</div>
					<div className="ai-credits-confirmation-right">
						<span
							className="ai-credits-confirmation-right-cancel"
							onClick={() => {
								setAiTemplateToSwitch(undefined);
							}}
						>
							Cancel
						</span>
						<span
							className="ai-credits-confirmation-right-confirm"
							onClick={() => {
								setShowAiLoadingModal(true);
								rotateTips();

								if (directEmail) {
									getAISnippets(
										[activeBusiness._id],
										listSelected?.id,
										aiTemplateToSwitch
									);
								} else {
									fetchingAiSnippets(aiTemplateToSwitch);
								}

								setSelectedTemplate(aiTemplateToSwitch);
								setAiTemplateToSwitch(undefined);
							}}
						>
							Confirm
						</span>
					</div>
				</div>
			)}
			<div
				className={`scheduled-send-failure-banner ${emailStatus === Status.FAILURE ? '' : 'hide'}`}
			>
				<ExclamationMarkBlack
					height={28}
					width={28}
					className="scheduled-send-failure-banner-icon"
				/>
				<span>
					We were unable to send your email. Please try again.
				</span>
			</div>
			<div className="emailForm-inner-wrapper">
				{size === 'md' &&
					selectedTemplate?.body?.includes('[ai_snippet]') &&
					((aiLoading &&
						!aiText?.[`${listSelected?.id}`]?.[
							`${selectedTemplate?.id}`
						]?.[`${activeBusiness?._id}`]) ||
						aiRegenerateLoading?.[`${listSelected?.id}`]?.[
							`${selectedTemplate?.id}`
						]?.[`${activeBusiness?._id}`]) && (
						<div className="ai-loading-form-background">
							<Loader status={Status.LOADING} />
						</div>
					)}
				{size === 'md' &&
					aiCredits <= 0 &&
					!aiText?.[`${listSelected?.id}`]?.[
						`${selectedTemplate?.id}`
					]?.[`${activeBusiness?._id}`] &&
					selectedTemplate?.body?.includes('[ai_snippet]') && (
						<>
							<div className="ai-credits-none-background" />
							<div className="ai-credits-none">
								You have used all your AI credits. Please choose
								a different template.
							</div>
						</>
					)}
				{emailTo !== undefined &&
					emailToSuggestions &&
					emailToSuggestions?.length > 0 && (
						<div className="emailForm-to relative mb-2 flex flex-col">
							<div className="flex items-center justify-between">
								<p className="mb-2 text-white">To:</p>
								<div>
									<button
										type="button"
										onClick={() => {
											if (
												scheduledEmailActivity[
													`${activeBusiness?._id}`
												]
											) {
												return;
											}
											setShowBCC(!showBCC);
											if (!showBCC) {
												setBcc(undefined);
											}
										}}
										className={classNames(
											'text-orange',
											'text-sm',
											{
												'cursor-not-allowed':
													scheduledEmailActivity[
														`${activeBusiness?._id}`
													]
											}
										)}
									>
										{!showBCC ? 'Add' : 'Remove'} BCC
									</button>
								</div>
							</div>
							<div
								data-freetier={isUserFreeTier}
								className="group data-[freetier=true]:select-none data-[freetier=true]:blur-sm"
							>
								<Autosuggest<EmailSuggestion>
									suggestions={emailSuggestions}
									getSuggestionValue={suggestion =>
										suggestion.email
									}
									focusInputOnSuggestionClick={false}
									shouldRenderSuggestions={() => true}
									highlightFirstSuggestion
									onSuggestionsClearRequested={noop}
									onSuggestionSelected={(
										e,
										{ suggestion }
									) => {
										if (isUserFreeTier) {
											handleOpenFreeTrialModal();
											return;
										}
										e.preventDefault();
										e.stopPropagation();
										if (isUserFreeTier) {
											handleOpenFreeTrialModal();
											return;
										}

										if (suggestion.email?.length > 0) {
											if (toInputRef.current) {
												toInputRef.current.blur();
											}

											setTo(
												String(
													suggestion.email
												).toLowerCase()
											);
										}
									}}
									renderSuggestion={({
										email,
										isPrimary
									}) => {
										if (isUserFreeTier) {
											return null; // Don't render anything for free-tier users
										}

										return (
											<span
												data-freetier={isUserFreeTier}
												className="flex w-full items-center justify-between px-4 py-4 data-[freetier=true]:select-none data-[freetier=true]:blur-sm"
											>
												{email}{' '}
												<span className="inline-flex items-center text-sm text-white">
													{isPrimary ? (
														<EmailPrimary
															width={20}
															className="mr-2 text-white"
														/>
													) : (
														<EmailSecondary
															width={15}
															className="mr-2 text-white"
														/>
													)}

													{isPrimary
														? 'Primary email'
														: 'Secondary email'}
												</span>
											</span>
										);
									}}
									onSuggestionsFetchRequested={noop}
									inputProps={{
										ref: toInputRef,
										className: classNames(
											'w-full text-base shadow-none rounded group data-[freetier=true]:select-none',
											{
												'disabled-to-input':
													scheduledEmailActivity[
														`${activeBusiness?._id}`
													]
											}
										),
										placeholder: 'Business email...',
										value: to,
										onCopy: e => {
											if (isUserFreeTier) {
												e.preventDefault(); // Blocks copy-paste
											}
										},
										onChange: (_, { newValue }) =>
											setTo(
												String(newValue).toLowerCase()
											)
									}}
									renderInputComponent={inputProps => (
										<div
											data-freetier={isUserFreeTier}
											className="emailForm-to--suggestion relative flex items-center justify-between data-[freetier=true]:cursor-default data-[freetier=true]:select-none data-[freetier=true]:blur-sm"
										>
											<input
												// eslint-disable-next-line react/jsx-props-no-spreading
												disabled={
													scheduledEmailActivity[
														`${activeBusiness?._id}`
													]
												}
												{...inputProps}
												onClick={e => {
													e.stopPropagation();
													if (isUserFreeTier) {
														handleOpenFreeTrialModal();
														return;
													}
													if (openTemplatesDropdown) {
														closeTemplatesDropdown(
															e
														);
													}
												}}
											/>

											<button
												type="button"
												onClick={e => {
													if (isUserFreeTier) {
														e.preventDefault();
														handleOpenFreeTrialModal();

														return;
													}

													toInputRef.current?.focus();
												}}
												className="emailForm-to--suggestion-arrow absolute cursor-pointer bg-transparent p-3 outline-none"
											>
												<DropDownDark />
											</button>
										</div>
									)}
								/>
							</div>
							{checkSoftBounced() && (
								<div className="emailForm-to-bounced">
									<BouncedEmail
										width={30}
										className="emailForm-to-bounced-icon"
									/>
									<span>
										This email might not go through.{' '}
									</span>
									<span className="emailForm-to-bounced-learn">
										Learn why
									</span>
									<div className="emailForm-to-bounced-overlay">
										This email has been flagged as possibly
										being inactive. If you send a message to
										this address, you can check your email
										inbox to make sure it does not bounce.
									</div>
								</div>
							)}
							<span className="emailForm-to-validation pt-1 text-sm">
								{validation.email &&
								validation.email?.length > 0
									? validation.email
									: ''}
							</span>
						</div>
					)}

				{emailTo !== undefined && showBCC && (
					<div className="emailForm-bcc relative mb-2 flex flex-col">
						<p className="mb-2 text-white">Bcc:</p>
						<input
							type="text"
							className={classNames(
								'w-full',
								'text-base',
								'shadow-none',
								'rounded',
								{
									'cursor-not-allowed': disabled
								}
							)}
							placeholder="Enter email..."
							value={bcc ?? ''}
							onClick={e => e.stopPropagation()}
							onChange={(e: ChangeEvent<HTMLInputElement>) =>
								setBcc(e.target.value)
							}
							disabled={disabled}
						/>
						<span className="pt-1 text-sm">
							{validation.bcc && validation.bcc?.length > 0
								? validation.bcc
								: ''}
						</span>
					</div>
				)}

				{emailTo === undefined && (
					<div className="emailForm-title relative mb-2 flex flex-col">
						<p className="mb-2 italic text-white">
							Email Template Title:
						</p>
						{size === 'lg' &&
							(selectedTemplate?.isStale ||
								staleTemplates?.[
									`${selectedTemplate?.id}`
								]) && (
								<span className="stale-template-indicator">
									<Stale />
									Stale
								</span>
							)}
						<input
							type="text"
							className={classNames(
								'emailForm-title-input',
								'w-full',
								'text-base',
								'shadow-none',
								'rounded',
								'italic',
								{
									'cursor-not-allowed': false
								}
							)}
							placeholder={name}
							value={titleInputValue}
							onClick={e => e.stopPropagation()}
							onChange={(e: ChangeEvent<HTMLInputElement>) => {
								setName(e.target.value);
								setTitleInputValue(e.target.value);
							}}
							disabled={disabled}
						/>
						<span className="pt-1 text-sm">
							{validation.name && validation.name?.length > 0
								? validation.name
								: ''}
						</span>
					</div>
				)}

				{emailSubjectForReply ? (
					''
				) : (
					<div className="emailForm-subject relative mb-2 flex flex-col">
						<span className="emailForm-subject-header">
							<p className="emailForm-subject-header-text mb-2 text-white">
								Email Subject:
							</p>
							{!disabled && (
								<span
									className="emailForm-subject-insert-text"
									onClick={() => subjectRef.current?.focus()}
								>
									<Popup
										trigger={
											<button
												type="button"
												className={classNames(
													'emailForm-subject-custom-text-button',
													'custom-orange-text',
													'text-base',
													'z-50',
													{ hidden: !forEditing }
												)}
											>
												Add custom text
											</button>
										}
										containerClass="px-2"
										contentClass="-my-3"
										onSelectionClose
										event={PopupTrigger.Click}
										align={PopupAlign.Right}
									>
										<button
											type="button"
											className="custom-text--item -mx-5 flex items-center whitespace-nowrap border-black px-4 py-2 text-left"
											onClick={() => {
												const firstPart = subject.slice(
													0,
													subjectRef.current
														?.selectionStart as number
												);
												const secondPart =
													subject.slice(
														subjectRef.current
															?.selectionStart as number
													);
												setSubject(
													`${firstPart}[business_name]${secondPart}`
												);
											}}
										>
											[business_name]
										</button>
										<button
											type="button"
											className="custom-text--item -mx-5 flex items-center whitespace-nowrap border-black px-4 py-2 text-left"
											onClick={() => {
												const firstPart = subject.slice(
													0,
													subjectRef.current
														?.selectionStart as number
												);
												const secondPart =
													subject.slice(
														subjectRef.current
															?.selectionStart as number
													);
												setSubject(
													`${firstPart}[list_name]${secondPart}`
												);
											}}
										>
											[list_name]
										</button>
									</Popup>
								</span>
							)}
						</span>
						<input
							type="text"
							className={classNames(
								'emailForm-subject-input',
								'w-full',
								'text-base',
								'shadow-none',
								'rounded',
								{
									disabled:
										scheduledEmailActivity[
											`${activeBusiness?._id}`
										]
								},
								{
									'cursor-not-allowed': disabled
								}
							)}
							disabled={
								scheduledEmailActivity[`${activeBusiness?._id}`]
							}
							placeholder="Your subject..."
							value={
								size === 'md'
									? replaceVariables(subject)
									: subject
							}
							ref={subjectRef}
							onClick={e => {
								e.stopPropagation();

								if (openTemplatesDropdown) {
									closeTemplatesDropdown(e);
								}
							}}
							onChange={(e: ChangeEvent<HTMLInputElement>) => {
								setSubject(e.target.value);
							}}
							disabled={disabled}
						/>
						<div
							className={`subjectLine-suggestion-div ${showSubjectLengthSuggestion ? '' : 'hide'} ${size === 'md' ? 'md' : ''} ${directEmail ? 'direct' : ''} `}
						>
							<div className="subjectLine-suggestion-inner-div">
								<img
									src={insightFilledColoredIcon}
									alt="icon-insight-filled"
								/>
								<span className="subjectLine-suggestion-text text-sm text-white">
									{`Email open rate increases by 12% for subject lines with ${subjectSuggestedLengthRange.start}-${subjectSuggestedLengthRange.end} words`}
								</span>
							</div>
							<button
								className="subjectLine-suggestion-button"
								type="button"
								onClick={() => {
									setShowSubjectLengthSuggestion(false);
									setEnableSubjectLengthSuggestion(false);
								}}
							>
								<CloseThin className="subjectLine-suggestion-icon-close text-grey" />
							</button>
						</div>
						<div
							className={`spam-suggestion-div ${showSpamSuggestion ? '' : 'hide'} ${size === 'md' ? 'md' : ''} ${directEmail ? 'direct' : ''} ${spamWordsInSubjectLine?.length > 1 ? 'taller' : ''} `}
						>
							<div className="spam-suggestion-inner-div">
								<img
									src={insightFilledColoredIcon}
									alt="icon-insight-filled"
								/>
								<span className="spam-suggestion-text text-sm text-white">
									{spamWordsMessage()}
								</span>
							</div>
							<button
								className="spam-suggestion-button"
								type="button"
								onClick={() => {
									setShowSpamSuggestion(false);
									setEnableSpamSuggestion(false);
								}}
							>
								<CloseThin className="spam-suggestion-icon-close text-grey" />
							</button>
						</div>
						<span>
							{validation.subject?.length > 0
								? validation.subject
								: ''}
						</span>
					</div>
				)}

				<div className="emailForm-body relative flex flex-1 flex-col">
					{!disabled && (
						<span className="z-10 mb-3 flex items-center justify-between">
							<p className="text-white">Message:</p>
							{size === 'md' &&
								(selectedTemplate?.isStale ||
									staleTemplates?.[
										`${selectedTemplate?.id}`
									]) && (
									<span className="stale-template-warning-message">
										This template has reached its sending
										limit.
										<QuestionMarkCircle className="stale-template-warning-icon" />
										<span className="stale-template-warning-hovertext">
											If an email template has been used
											more than 500 times, it will be
											marked as "stale".
											<br />
											<br />
											In order to prevent spam, you are
											not allowed to send or schedule an
											email using a stale template. Please
											change
											<span>
												{' '}
												at least 20% of the body of your
												email, and at least 50% of the
												words in the subject.
											</span>
											<br />
											<br />
											You can select another template from
											the template dropdown menu; or click
											“Back to template” on the top left
											corner to edit the current template.
										</span>
									</span>
								)}
							<span className="emailForm-body-insert-text">
								<Popup
									trigger={
										<button
											type="button"
											className={classNames(
												'custom-orange-text',
												'text-base',
												'z-50',
												{ hidden: !forEditing }
											)}
										>
											Add custom text
										</button>
									}
									containerClass="px-2"
									contentClass="-my-3"
									onSelectionClose
									event={PopupTrigger.Click}
									align={PopupAlign.Right}
								>
									<button
										type="button"
										className="custom-text--item -mx-5 flex items-center whitespace-nowrap border-black px-4 py-2 text-left"
										onClick={() =>
											setBodyContextMenuValue(
												'[business_name]'
											)
										}
									>
										[business_name]
									</button>
									<button
										type="button"
										className="custom-text--item -mx-5 flex items-center whitespace-nowrap border-black px-4 py-2 text-left"
										onClick={() =>
											setBodyContextMenuValue(
												'[list_name]'
											)
										}
									>
										[list_name]
									</button>
								</Popup>
								<button
									type="button"
									className={`ai-button snippet ${
										body
											.replace(/(<([^>]+)>)/gi, '')
											.replace(/\s+/g, '')
											.replace(/&nbsp;/g, '')?.length ===
											0 && size === 'lg'
											? 'disabled'
											: ''
									} ${size === 'md' ? 'hide' : ''} ${body.includes('[ai_snippet]') && size !== 'md' ? 'added' : ''} `}
									disabled={
										body
											.replace(/(<([^>]+)>)/gi, '')
											.replace(/\s+/g, '')
											.replace(/&nbsp;/g, '')?.length ===
											0 && size === 'lg'
									}
									onClick={() => {
										if (body.includes('[ai_snippet]')) {
											setBody(
												body.replace('[ai_snippet]', '')
											);

											return;
										}

										setBodyContextMenuValue('[ai_snippet]');
									}}
								>
									{body.includes('[ai_snippet]') &&
									size !== 'md'
										? 'Remove AI Snippet'
										: 'Add AI Snippet'}
									<ChatGPT />
								</button>
								<button
									type="button"
									className={`ai-button regenerate ${
										size === 'md' &&
										aiCredits <= 0 &&
										aiRegenerated?.[
											`${listSelected?.id}`
										]?.[`${selectedTemplate?.id}`]?.[
											`${activeBusiness?._id}`
										]
											? 'disabled'
											: ''
									} ${size === 'lg' ? 'hide' : ''} ${
										size === 'md' &&
										(!selectedTemplate?.body?.includes(
											'[ai_snippet]'
										) ||
											scheduledEmailActivity[
												`${activeBusiness?._id}`
											])
											? 'hide'
											: ''
									} ${
										size === 'md' &&
										!aiRegenerated?.[
											`${listSelected?.id}`
										]?.[`${selectedTemplate?.id}`]?.[
											`${activeBusiness?._id}`
										]
											? 'free'
											: ''
									} ${
										size === 'md' &&
										aiCredits > 0 &&
										aiRegenerated?.[
											`${listSelected?.id}`
										]?.[`${selectedTemplate?.id}`]?.[
											`${activeBusiness?._id}`
										]
											? 'not-free'
											: ''
									} `}
									onClick={() => {
										if (
											activeBusiness &&
											selectedTemplate
										) {
											regenAISnippet(
												activeBusiness._id,
												listSelected?.id,
												selectedTemplate,
												aiText?.[
													`${listSelected?.id}`
												]?.[
													`${selectedTemplate?.id}`
												]?.[`${activeBusiness?._id}`]
													?.id
											);

											sendAnalytics({
												type: 'track',
												userId: user.email,
												eventName: `Regenerated AI Snippet`,
												properties: {
													free: !aiRegenerated?.[
														`${listSelected?.id}`
													]?.[
														`${selectedTemplate?.id}`
													]?.[
														`${activeBusiness?._id}`
													]
												}
											});
										}
									}}
									disabled={
										size === 'md' &&
										aiCredits <= 0 &&
										aiRegenerated?.[
											`${listSelected?.id}`
										]?.[`${selectedTemplate?.id}`]?.[
											`${activeBusiness?._id}`
										]
									}
								>
									Regenerate AI Snippet
									<ChatGPT />
									{aiRegenerateFailure && (
										<span className="ai-button-failure-text">
											<ExclamationMarkRed className="ai-button-failure-icon" />
											Regeneration failed. Try again.
										</span>
									)}
								</button>
								{size === 'md' &&
									aiText?.[`${listSelected?.id}`]?.[
										`${selectedTemplate?.id}`
									]?.[`${activeBusiness?._id}`] &&
									!aiRegenerated?.[`${listSelected?.id}`]?.[
										`${selectedTemplate?.id}`
									]?.[`${activeBusiness?._id}`] &&
									!scheduledEmailActivity[
										`${activeBusiness?._id}`
									] && (
										<span className="ai-regenerate-free">
											1st free!
										</span>
									)}
								<div className="ai-button-snippet-hovertext">
									<span className="ai-button-snippet-hovertext-header">
										Add AI Snippet
									</span>
									<span className="ai-button-snippet-hovertext-body">
										Insert a snippet of AI-generated,
										context-based text in order to customize
										each email which will help you avoid
										spam filters. Maximum of 1 snippet per
										email.
									</span>
								</div>
								{size === 'md' &&
								aiCredits <= 0 &&
								aiRegenerated?.[`${listSelected?.id}`]?.[
									`${selectedTemplate?.id}`
								]?.[`${activeBusiness?._id}`] ? (
									<div className="ai-regenerate-no-credits-hovertext">
										AI credits exhausted. Credits refresh at
										the start of each month. To increase
										your credit limit, please contact our
										customer support team.
									</div>
								) : (
									<div className="ai-regenerate-hovertext">
										<span className="ai-regenerate-hovertext-header">
											Regenerate Snippet
										</span>
										<span className="ai-regenerate-hovertext-body">
											You can regenerate your snippet to
											try something new! The first
											regeneration per email is free.
										</span>
									</div>
								)}
							</span>
						</span>
					)}

					<ContextMenu
						disabled
						offset={{ top: 32 }}
						onSelect={(item: string) =>
							setBodyContextMenuValue(item)
						}
						items={['business_name', 'list_name']}
						className="h-full rounded"
					>
						{() => (
							<RichEditor
								value={
									size === 'md'
										? replaceVariables(body)
										: body
								}
								onChange={setBody}
								className={classNames(
									'rounded',
									'h-full',
									forEditing
										? 'emailForm-template-message'
										: 'emailForm-template-email-message',
									{ [`RichEditor-${size}`]: !!size },
									{
										'cursor-not-allowed': disabled
									}
								)}
								disabled={
									disabled ||
									scheduledEmailActivity[
										`${activeBusiness?._id}`
									]
								}
								contextMenuValue={bodyContextMenuValue}
								onFocus={e => {
									if (openTemplatesDropdown) {
										closeTemplatesDropdown(e);
									}
								}}
							/>
						)}
					</ContextMenu>
					<span className="pt-1 text-sm">
						{validation.body?.length > 0 ? validation.body : ''}
					</span>
				</div>
			</div>
		</div>
	);
}
